import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import { absUrl } from "@/lib/api";

const CATEGORIES = ["Bespoke", "Adventure", "Cultural"];

const toAbs = absUrl;

export default function PartnerPortal() {
  const { t } = useTranslation();
  const [tours, setTours] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", duration: "", price_usd: 1000,
    max_participants: 10, destination: "Bali", category: "Bespoke",
  });
  const [coverUrl, setCoverUrl] = useState("");
  const [galleryUrls, setGalleryUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try { setTours((await api.get("/partner/tours")).data || []); } catch {}
  };
  useEffect(() => { load(); }, []);

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post("/upload-image", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return r.data.url;
  };

  const onCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadOne(file);
      setCoverUrl(url);
      toast.success("Cover uploaded");
    } catch { toast.error("Cover upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const onGallery = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const urls = await Promise.all(files.map(uploadOne));
      setGalleryUrls((prev) => [...prev, ...urls]);
      toast.success(`${urls.length} image${urls.length > 1 ? "s" : ""} uploaded`);
    } catch { toast.error("Some uploads failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const removeGallery = (i) => setGalleryUrls((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!coverUrl) { toast.error("Upload a cover image first"); return; }
    setSubmitting(true);
    try {
      await api.post("/tours", {
        ...form,
        price_usd: parseFloat(form.price_usd),
        max_participants: parseInt(form.max_participants),
        cover_image_url: coverUrl,
        images: galleryUrls,
      });
      toast.success("Tour submitted. Pending admin approval.");
      setForm({ ...form, title: "", description: "", duration: "" });
      setCoverUrl("");
      setGalleryUrls([]);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar />
      <main className="pt-28 pb-24 max-w-6xl mx-auto px-6 md:px-12" data-testid="partner-portal-page">
        <div className="t-overline text-seafoam mb-5">Partner Portal</div>
        <h1 className="t-display text-slate-900">
          Submit a new <b>tour package.</b>
        </h1>

        <form onSubmit={submit} className="mt-12 bg-white rounded-2xl p-8 shadow-sm grid md:grid-cols-2 gap-5" data-testid="tour-create-form">
          <label className="block md:col-span-2">
            <span className="t-overline text-slate-500">Title</span>
            <input data-testid="tour-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white outline-none focus:border-seafoam" />
          </label>

          <label className="block md:col-span-2">
            <span className="t-overline text-slate-500">Description</span>
            <textarea data-testid="tour-desc" required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white outline-none focus:border-seafoam resize-none" />
          </label>

          <label className="block">
            <span className="t-overline text-slate-500">Duration</span>
            <input data-testid="tour-duration" required placeholder="e.g. 5 days / 4 nights" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white outline-none focus:border-seafoam" />
          </label>

          <label className="block">
            <span className="t-overline text-slate-500">Destination</span>
            <input data-testid="tour-destination" required value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white outline-none focus:border-seafoam" />
          </label>

          <label className="block">
            <span className="t-overline text-slate-500">Price (USD)</span>
            <input data-testid="tour-price" type="number" min={1} required value={form.price_usd} onChange={(e) => setForm({ ...form, price_usd: e.target.value })} className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white outline-none focus:border-seafoam" />
          </label>

          <label className="block">
            <span className="t-overline text-slate-500">Max participants</span>
            <input data-testid="tour-max" type="number" min={1} required value={form.max_participants} onChange={(e) => setForm({ ...form, max_participants: e.target.value })} className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white outline-none focus:border-seafoam" />
          </label>

          <label className="block md:col-span-2">
            <span className="t-overline text-slate-500">Category</span>
            <select data-testid="tour-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white outline-none focus:border-seafoam">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>

          {/* Cover image upload */}
          <div className="md:col-span-2">
            <div className="t-overline text-slate-500 mb-2">Cover image</div>
            <div className="flex items-start gap-4">
              {coverUrl ? (
                <div className="relative w-40 h-32 rounded-xl overflow-hidden border border-slate-200">
                  <img src={toAbs(coverUrl)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setCoverUrl("")} className="absolute top-1 right-1 p-1 rounded-full bg-white/95 text-slate-700 shadow" aria-label="remove">
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <label className="w-40 h-32 rounded-xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400 cursor-pointer hover:border-seafoam hover:text-seafoam transition-colors">
                  <ImageIcon size={20} strokeWidth={1.5} />
                  <span className="text-xs mt-2">Upload cover</span>
                  <input data-testid="tour-cover-input" type="file" accept="image/*" onChange={onCover} className="hidden" />
                </label>
              )}
            </div>
          </div>

          {/* Multi gallery */}
          <div className="md:col-span-2">
            <div className="t-overline text-slate-500 mb-2">Gallery images (multi-select)</div>
            <div className="flex flex-wrap gap-3 items-start">
              {galleryUrls.map((u, i) => (
                <div key={i} className="relative w-28 h-28 rounded-xl overflow-hidden border border-slate-200">
                  <img src={toAbs(u)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeGallery(i)} className="absolute top-1 right-1 p-1 rounded-full bg-white/95 text-slate-700 shadow" aria-label="remove">
                    <X size={12} />
                  </button>
                </div>
              ))}
              <label className="w-28 h-28 rounded-xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400 cursor-pointer hover:border-seafoam hover:text-seafoam transition-colors">
                <Upload size={18} strokeWidth={1.5} />
                <span className="text-[10px] mt-2 uppercase tracking-wider">Add images</span>
                <input data-testid="tour-gallery-input" type="file" accept="image/*" multiple onChange={onGallery} className="hidden" />
              </label>
            </div>
            {uploading && <div className="text-xs text-slate-400 mt-3">Uploading…</div>}
          </div>

          <button data-testid="tour-submit" disabled={submitting || uploading} type="submit" className="md:col-span-2 btn-pill bg-slate-900 text-white hover:bg-slate-800 justify-center disabled:opacity-50">
            {submitting ? "Submitting…" : "Submit for approval"}
          </button>
        </form>

        <div className="mt-16">
          <div className="t-overline text-seafoam mb-4">Your tours</div>
          <div className="grid md:grid-cols-2 gap-6" data-testid="partner-tours-list">
            {tours.length === 0 && <div className="text-slate-500">Nothing submitted yet.</div>}
            {tours.map((tr) => (
              <div key={tr.tour_id} className="bg-white rounded-2xl overflow-hidden shadow-sm flex">
                <div className="w-32 shrink-0 bg-slate-100"><img src={toAbs(tr.cover_image_url)} alt="" className="w-full h-full object-cover" /></div>
                <div className="p-5 flex-1">
                  <div className="t-overline text-slate-500">{tr.category} · {tr.destination}</div>
                  <div className="text-base font-medium mt-1 text-slate-900"><b>{tr.title}</b></div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-sm text-slate-600">${tr.price_usd.toLocaleString()}</span>
                    <span className={`text-xs px-3 py-1 rounded-full ${tr.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{tr.status}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
